import {
    getRangeLabels,
    getTimeLineLabels,
    mapFromElementRangeArray,
    TimeSeriesDisableRange,
    TimeSeriesDisableRangeLabel,
    TimeSeriesSessionLabelDataItem,
} from 'utils/timeseries/TimeSeriesDisableRange';
import { calculateTimeDomain } from 'utils/components/TimeSeriesChart/timeSeriesTimeScale';
import { usePortfolioPlStreamData } from 'widgets/portfolioPl/store/stream/streamSlice';
import {
    PortfolioPlDataState,
    PortfolioPlDataTimeSeriesItem,
} from 'widgets/portfolioPl/store/stream/PortfolioPlWidgetStreamState';
import { fillPosNegGaps } from 'utils/components/TimeSeriesChart/posNegGradientLine';
import { TimeLineLabel } from 'utils/components/TimeSeriesChart/timeline';
import sortBy from 'lodash/sortBy';
import omitUndefined from 'utils/omitUndefined';
import { usePortfolioPlConfigSlice } from 'widgets/portfolioPl/store/config/configSlice';
import {
    PortfolioPlAggregationType,
    PortfolioPlWidgetConfigState,
} from 'widgets/portfolioPl/store/config/PortfolioPlWidgetConfigState';
import {
    getInterpolatedValue,
    InterpolationContext,
    isValueWithInterpolation,
    toValueWithInterpolation,
    ValueWithInterpolation,
} from 'utils/interpolation';
import { NumberFormat } from '@instinet-analytics/core-lib';
import { getTimeDomainEnd } from 'utils/timeseries/timeSeriesUtils';
import { useSelectAppSettings } from 'app/store/appSettings/selectors';
import { CurrencyFormat } from '@instinet-analytics/core-lib';

const MS_IN_MINUTE = 60 * 1000;

export interface PortfolioPlDataItem extends TimeSeriesSessionLabelDataItem {
    _i: number;
    time: number;
    realizedPnl?: number;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'pos-realizedPnl'?: number;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'neg-realizedPnl'?: number;
    totalPnl?: number;
    unrealizedPnl?: number;
    indexPnl?: ValueWithInterpolation;
    buyLeaves?: number;
    sellLeaves?: number;
    netLeaves?: number;
    isNull: boolean;
}

export interface PortfolioPlData {
    breaksInTrading: TimeSeriesDisableRange[];
    rangeLabels: TimeSeriesDisableRangeLabel[];
    timeDomain: [number, number];
    timeSeries: PortfolioPlDataItem[];
    timeLineLabels: TimeLineLabel[];
}

export const initialData: PortfolioPlData = {
    breaksInTrading: [],
    rangeLabels: [],
    timeDomain: [0, 1],
    timeSeries: [],
    timeLineLabels: [],
};

export function usePortfolioPlData(): PortfolioPlData {
    const streamState = usePortfolioPlStreamData();
    const { config } = usePortfolioPlConfigSlice();
    const { showBuysAsPositive } = useSelectAppSettings();

    if (!streamState) {
        return initialData;
    }

    return getPortfolioPlData(streamState, config, showBuysAsPositive);
}

export function getPortfolioPlData(
    streamState: PortfolioPlDataState,
    configState: PortfolioPlWidgetConfigState,
    showBuysAsPositive: boolean
): PortfolioPlData {
    const { timeSeries, disableRange } = streamState;
    const { aggregationType } = configState;

    if (timeSeries.length === 0) {
        return initialData;
    }

    const interpolationContext = new InterpolationContext<PortfolioPlDataItem>(
        'forward'
    );
    const mappedTimeSeries: PortfolioPlDataItem[] = timeSeries.map((t) =>
        mapTimeSeriesItem(t, aggregationType, showBuysAsPositive)
    );
    const haveRealized = mappedTimeSeries.find(
        (d) => d.realizedPnl !== undefined
    );
    if (haveRealized) {
        for (let i = 0; i < mappedTimeSeries.length; i++) {
            if (mappedTimeSeries[i].realizedPnl !== undefined) {
                break;
            }
            if (
                !mappedTimeSeries[i].isNull &&
                mappedTimeSeries[i].realizedPnl === undefined
            ) {
                mappedTimeSeries[i]['pos-realizedPnl'] = 0;
            }
        }
    }

    const filledData = populateEmptyItems(
        fillPosNegGaps(mappedTimeSeries, 'realizedPnl', [
            'totalPnl',
            'unrealizedPnl',
        ])
    );

    const indexTimeDomainEnd = getTimeDomainEnd(timeSeries, ['indexPnl']);
    const indexData = filledData.filter((i) => i._i <= indexTimeDomainEnd);

    for (let i = 0; i < indexData.length; i++) {
        interpolationContext.interpolateLinear(filledData, ['indexPnl'], i);
    }

    const timeDomain = calculateTimeDomain(filledData);
    if (timeDomain && timeDomain[1]) {
        const padding = 10000;
        timeDomain[1] = timeDomain[1] + padding;
    }

    const breaksInTrading = mapFromElementRangeArray(disableRange);
    const rangeLabels = getRangeLabels(timeSeries, breaksInTrading);
    const timeLineLabels = getTimeLineLabels(
        timeSeries,
        breaksInTrading,
        filledData
    );

    return {
        breaksInTrading,
        rangeLabels,
        timeLineLabels,
        timeSeries: filledData,
        timeDomain,
    };
}

export interface PortfolioPlLegendData {
    realizedPnl?: string;
    unrealizedPnl?: string;
    totalPnl?: string;
    indexPnl?: string;
    buyLeaves?: string;
    sellLeaves?: string;
    netLeaves?: string;
}

interface LegendDataValues {
    realizedPnl?: number;
    ['pos-realizedPnl']?: number;
    totalPnl?: number;
    unrealizedPnl?: number;
    indexPnl?: ValueWithInterpolation;
    buyLeaves?: number;
    sellLeaves?: number;
    netLeaves?: number;
}

export function deriveLegendData(
    time: number | undefined,
    data: PortfolioPlDataItem[],
    numberFormat: NumberFormat,
    currencyFormat: CurrencyFormat
): PortfolioPlLegendData {
    let dataValues: LegendDataValues | undefined = undefined;

    if (time === undefined) {
        dataValues = getLatestWithValues(data);
    } else {
        const beforeTime = time && data.filter((d) => d.time <= time!);

        dataValues = beforeTime ? beforeTime[beforeTime.length - 1] : undefined;
    }

    if (!dataValues) {
        return {};
    }

    const realizedPnl =
        dataValues.realizedPnl === undefined &&
        dataValues['pos-realizedPnl'] === 0
            ? 0
            : dataValues.realizedPnl;

    return {
        realizedPnl: getLegendValue(realizedPnl, numberFormat),
        unrealizedPnl: getLegendValue(dataValues.unrealizedPnl, numberFormat),
        totalPnl: getLegendValue(dataValues.totalPnl, numberFormat),
        indexPnl: getLegendValue(
            getInterpolatedValue(dataValues.indexPnl),
            numberFormat
        ),
        buyLeaves: getCurrencyLegendValue(
            dataValues?.buyLeaves,
            currencyFormat
        ),
        sellLeaves: getCurrencyLegendValue(
            dataValues?.sellLeaves,
            currencyFormat
        ),
        netLeaves: getCurrencyLegendValue(
            dataValues?.netLeaves,
            currencyFormat
        ),
    };
}

function getLatestWithValues(data: PortfolioPlDataItem[]): LegendDataValues {
    let populatedCount = 0;
    const ret: LegendDataValues = {};

    const copyNumberIfMissing = (
        key:
            | 'realizedPnl'
            | 'unrealizedPnl'
            | 'totalPnl'
            | 'buyLeaves'
            | 'sellLeaves'
            | 'netLeaves',
        item: PortfolioPlDataItem
    ) => {
        if (item[key] !== undefined && ret[key] === undefined) {
            ret[key] = item[key];
            populatedCount++;
        }
    };

    for (let i = data.length - 1; i >= 0; i--) {
        const item = data[i];

        copyNumberIfMissing('realizedPnl', item);
        copyNumberIfMissing('unrealizedPnl', item);
        copyNumberIfMissing('totalPnl', item);
        copyNumberIfMissing('buyLeaves', item);
        copyNumberIfMissing('sellLeaves', item);
        copyNumberIfMissing('netLeaves', item);

        if (
            isValueWithInterpolation(item.indexPnl) &&
            !item.indexPnl.isInterpolated &&
            ret.indexPnl === undefined
        ) {
            ret.indexPnl = item.indexPnl;
            populatedCount++;
        }

        if (populatedCount === 7) {
            return ret;
        }
    }
    return ret;
}

function mapTimeSeriesItem(
    item: PortfolioPlDataTimeSeriesItem,
    aggregationType: PortfolioPlAggregationType,
    showBuysAsPositive: boolean
): PortfolioPlDataItem {
    const ret: PortfolioPlDataItem = {
        _i: item._i,
        time: item._i * MS_IN_MINUTE,
        isNull: true,
    };

    const { realizedPnl, totalPnl, unrealizedPnl } =
        extractValuesByAggregationType(item, aggregationType);
    const { indexPnl } = item;

    ret.isNull = !(
        realizedPnl !== undefined ||
        totalPnl !== undefined ||
        unrealizedPnl !== undefined
    );

    ret.totalPnl = totalPnl;
    ret.unrealizedPnl = unrealizedPnl;

    ret.realizedPnl = realizedPnl;
    ret['pos-realizedPnl'] =
        realizedPnl !== undefined && realizedPnl > 0 ? realizedPnl : undefined;
    ret['neg-realizedPnl'] =
        realizedPnl !== undefined && realizedPnl < 0 ? realizedPnl : undefined;

    ret.indexPnl = toValueWithInterpolation(indexPnl);

    mapLeaves(ret, item, aggregationType, showBuysAsPositive);

    return ret;
}

function extractValuesByAggregationType(
    item: PortfolioPlDataTimeSeriesItem,
    aggregationType: PortfolioPlAggregationType
) {
    switch (aggregationType) {
        case 'buys':
            return {
                realizedPnl: item.realizedPnlBuys,
                totalPnl: item.totalPnlBuys,
                unrealizedPnl: item.unrealizedPnlBuys,
            };
        case 'sells':
            return {
                realizedPnl: item.realizedPnlSells,
                totalPnl: item.totalPnlSells,
                unrealizedPnl: item.unrealizedPnlSells,
            };
        default:
            return {
                realizedPnl: item.realizedPnlBuysAndSells,
                totalPnl: item.totalPnlBuysAndSells,
                unrealizedPnl: item.unrealizedPnlBuysAndSells,
            };
    }
}

function mapLeaves(
    ret: PortfolioPlDataItem,
    item: PortfolioPlDataTimeSeriesItem,
    aggregationType: PortfolioPlAggregationType,
    showBuysAsPositive: boolean
) {
    const { buyUnrealizedBenchmark, sellUnrealizedBenchmark } = item;

    const buyPosFactor = showBuysAsPositive ? -1 : 1;
    ret.buyLeaves =
        buyUnrealizedBenchmark !== undefined
            ? -1 * buyUnrealizedBenchmark * buyPosFactor
            : undefined;
    ret.sellLeaves =
        sellUnrealizedBenchmark !== undefined
            ? sellUnrealizedBenchmark * buyPosFactor
            : undefined;

    switch (aggregationType) {
        case 'combined': {
            ret.netLeaves =
                ret.buyLeaves !== undefined || ret.sellLeaves !== undefined
                    ? (ret.buyLeaves ?? 0) + (ret.sellLeaves ?? 0)
                    : undefined;
            break;
        }
        case 'buys': {
            ret.netLeaves = ret.buyLeaves;
            break;
        }
        case 'sells': {
            ret.netLeaves = ret.sellLeaves;
        }
    }
}

function populateEmptyItems(
    data: PortfolioPlDataItem[]
): PortfolioPlDataItem[] {
    const itemsToAdd: PortfolioPlDataItem[] = [];
    if (data.length < 2) {
        return data;
    }

    const pnlTimeDomainEnd = getTimeDomainEnd(data, [
        'realizedPnl',
        'unrealizedPnl',
        'totalPnl',
    ]);

    let prevWithValue = data.find((i) => i.realizedPnl !== undefined);
    if (!prevWithValue) {
        return data;
    }
    const startIdx = data.indexOf(prevWithValue) + 1;

    const inDomain = data.filter((i) => i._i <= pnlTimeDomainEnd);
    const toIndex = data.indexOf(inDomain[inDomain.length - 1]) ?? data.length;

    for (let i = startIdx; i <= toIndex; i++) {
        if (data[i].realizedPnl === undefined) {
            data[i] = {
                ...prevWithValue,
                ...omitUndefined(data[i]),
            };
        }

        prevWithValue = data[i];
    }
    return sortBy([...data, ...itemsToAdd], 'time');
}

function getLegendValue(value: number | undefined, numberFormat: NumberFormat) {
    return value !== undefined ? numberFormat.n2(value) : undefined;
}

function getCurrencyLegendValue(
    value: number | undefined,
    currencyFormat: CurrencyFormat
) {
    return value !== undefined
        ? currencyFormat.c0FmtFxWithPrefix(value)
        : undefined;
}
